import React, {useEffect, useMemo, useState} from 'react'
import Cookies from 'js-cookie'
import {DragDropContext, Draggable, Droppable, DropResult} from 'react-beautiful-dnd'
import {
  ColumnDef,
  ColumnOrderState,
  SortingFn,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  sortingFns,
  useReactTable,
} from '@tanstack/react-table'
import {QueryGetEmpreendimentosRelatorioCompleto} from '../../../graphql'
import Pagination from '../../../components/UI/Pagination'
import {Loader} from '../../../components/UI/Loader/Loader'
import {KTSVG} from '../../../../_metronic/helpers'
import {formatDate, formatNumber} from '../../explorer/components/common'
import {BiExport, BiInfoCircle} from 'react-icons/bi'
import {ExportTable} from '../../../utils/ExportXLSX'

type Props = {
  active: boolean
  filtro: any[]
  toggleFilter: any
  toggleInfos: any
  objectFilter: any
}

const TABLE_CONFIG_COOKIE = 'table_relatorio_empreendimento_config'
const COOKIE_OPTIONS = {expires: 365, sameSite: 'lax' as const}
const defaultColumnOrder = [
  'created_at',
  'temperatura',
  'nome',
  'status',
  'qtd_lotes',
  'terreno',
  'privativa',
  'zona',
  'valor_terreno',
  'vgv',
  'vgv_proprio',
  'vgv_vp',
  'terreno_vgv',
  'terreno_dinheiro_vgv',
  'terreno_dinheiro',
  'valor_comissao',
  'operacao_urbana',
  'responsavel',
  'corretor',
]

const normalizeColumnOrder = (savedColumnOrder: any) => {
  if (!Array.isArray(savedColumnOrder)) {
    return defaultColumnOrder
  }

  const savedValidColumns = savedColumnOrder.filter((columnId) =>
    defaultColumnOrder.includes(columnId)
  )
  const missingColumns = defaultColumnOrder.filter(
    (columnId) => !savedValidColumns.includes(columnId)
  )

  return [...savedValidColumns, ...missingColumns]
}

const getSavedTableConfig = () => {
  const savedConfig = Cookies.get(TABLE_CONFIG_COOKIE)

  if (!savedConfig) {
    return {
      columnOrder: defaultColumnOrder,
      columnVisibility: {},
    }
  }

  try {
    const parsedConfig = JSON.parse(savedConfig)

    return {
      columnOrder: normalizeColumnOrder(parsedConfig.columnOrder),
      columnVisibility: parsedConfig.columnVisibility ?? {},
    }
  } catch (error) {
    return {
      columnOrder: defaultColumnOrder,
      columnVisibility: {},
    }
  }
}

const getPrimeiroTerreno = (row) => {
  return row.empreendimento_terreno?.length > 0 ? row.empreendimento_terreno[0].terreno : null
}

const getTemperaturaValue = (row) => {
  const temperatura = row.empreendimento_temperatura?.nome

  if (!temperatura) {
    return 0
  }

  if (isNaN(temperatura)) {
    return temperatura === '' ? 0 : temperatura
  }

  return Number(temperatura)
}

const temperaturaSort: SortingFn<any> = (rowA, rowB, columnId) => {
  const valueA = rowA.getValue(columnId)
  const valueB = rowB.getValue(columnId)
  const isEmptyA = valueA === null || valueA === undefined || valueA === ''
  const isEmptyB = valueB === null || valueB === undefined || valueB === ''

  if (isEmptyA && isEmptyB) return 0
  if (isEmptyA) return 1
  if (isEmptyB) return -1

  return sortingFns.alphanumeric(rowA, rowB, columnId)
}

const Table: React.FC<Props> = ({active, filtro, toggleFilter, toggleInfos, objectFilter}) => {
  let result

  if (active === true) {
    result = 0
  } else if (active === false) {
    result = 1
  }

  const savedTableConfig = useMemo(() => getSavedTableConfig(), [])
  const [pages, setPages] = useState(0)
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(savedTableConfig.columnOrder)
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    savedTableConfig.columnVisibility
  )
  const tableContainerWidth = Math.max(
    window.innerWidth - (window.innerWidth > 991 ? 177 : 32),
    320
  )

  const {data} = QueryGetEmpreendimentosRelatorioCompleto({
    variables: {
      pagination: {pagina: pages, quantidade: 1000},
      filtro: {
        fields: filtro,
        objectFilter: {
          ...{
            empreendimento_terreno:
              objectFilter.id_corretor?.objectFilter?.empreendimento_terreno || undefined,
          },
          ...{users: objectFilter.id_equipe?.objectFilter?.users || undefined},
        },
      },
      // situation: result,
    },
  })

  const tableData = useMemo(() => data?.GetEmpreendimentos?.result ?? [], [data])

  const columns = useMemo<ColumnDef<any>[]>(
    () => [

      {
        id:'created_at',
        header:'Data Cadastro',
        accessorFn: (row) => row.created_at,
        cell: ({row}) => row.original.created_at ? formatDate(row.original.created_at) : '-',
      },
      {
        id: 'temperatura',
        header: 'Temperatura',
        accessorFn: getTemperaturaValue,
        cell: ({row}) =>
          row.original.empreendimento_temperatura ? (
            <span
              className='badge badge-temperatura fw-bold me-auto px-4 py-2 p-5'
              style={{
                backgroundColor: row.original.empreendimento_temperatura?.cor,
              }}
            >
              {row.original.empreendimento_temperatura?.nome}
            </span>
          ) : (
            ''
          ),
        sortingFn: temperaturaSort,
      },
      {
        accessorKey: 'nome',
        header: 'Nome',
        cell: ({row}) => (
          <a
            href={`/empreendimentos/details/${row.original.id}#composicao-terrenos`}
            target='_blank'
            rel='noreferrer'
            className='fs-6 text-decoration-none color-dark'
          >
            {row.original.nome ? row.original.nome : 'Não informado'}
          </a>
        ),
      },
      {
        id: 'status',
        accessorFn: (row) => row.empreendimento_situacao?.nome ?? '-',
        header: 'Status',
        cell: ({getValue}) => getValue() as React.ReactNode,
      },
      {
        id: 'qtd_lotes',
        accessorFn: (row) => row.empreendimento_terreno?.length ?? 0,
        header: 'Qtd. Lotes',
        cell: ({getValue}) => getValue() as React.ReactNode,
      },
      {
        id: 'terreno',
        accessorFn: (row) => row.area_total_terreno,
        header: 'Terreno(m²)',
        cell: ({row}) =>
          row.original.area_total_terreno
            ? formatNumber(row.original.area_total_terreno, 'inteiro')
            : '-',
      },
      {
        id: 'privativa',
        accessorFn: (row) => row.area_total_privativa,
        header: 'Privativa (m²)',
        cell: ({row}) =>
          row.original.area_total_privativa
            ? formatNumber(row.original.area_total_privativa, 'inteiro')
            : '-',
      },
      {
        id: 'zona',
        accessorFn: (row) => getPrimeiroTerreno(row)?.zoneamento ?? '-',
        header: 'Zona',
        cell: ({getValue}) => getValue() as React.ReactNode,
      },
      {
        id: 'valor_terreno',
        accessorFn: (row) => row.preco_medio_m2 * row.area_total_terreno,
        header: 'Valor do terreno',
        cell: ({row}) =>
          row.original.preco_medio_m2
            ? formatNumber(
                row.original.preco_medio_m2 * row.original.area_total_terreno,
                'currency_inteiro'
              )
            : '-',
      },
      {
        accessorKey: 'vgv',
        header: 'VGV',
        cell: ({row}) =>
          row.original.vgv ? formatNumber(row.original.vgv, 'currency_inteiro') : '-',
      },
      {
        accessorKey: 'vgv_proprio',
        header: 'VGV Próprio',
        cell: ({row}) =>
          row.original.vgv_proprio ? formatNumber(row.original.vgv_proprio, 'currency_inteiro') : '-',
      },
      {
        accessorKey: 'vgv_vp',
        header: 'VGV-VP',
        cell: ({row}) =>
          row.original.vgv_vp ? formatNumber(row.original.vgv_vp, 'currency') : '-',
      },
      {
        id: 'terreno_vgv',
        accessorFn: (row) =>
          row.vgv ? ((row.preco_medio_m2 * row.area_total_terreno) / (row.vgv || 1)) * 100 : null,
        header: 'Terreno / VGV',
        cell: ({getValue}) =>
          getValue() ? `${formatNumber(getValue() as number, 'inteiro')}%` : '-',
      },
      {
        id: 'terreno_dinheiro_vgv',
        accessorFn: (row) => (row.vgv_vp ? (row.terreno_em_dinheiro / (row.vgv || 1)) * 100 : null),
        header: 'Terreno em dinheiro/VGV',
        cell: ({getValue}) =>
          getValue() ? `${formatNumber(getValue() as number, 'inteiro')}%` : '-',
      },
      {
        accessorKey: 'terreno_em_dinheiro',
        header: 'Terreno em dinheiro',
        cell: ({row}) =>
          row.original.terreno_em_dinheiro
            ? formatNumber(row.original.terreno_em_dinheiro, 'currency')
            : '-',
      },
      {
        id: 'valor_comissao',
        accessorFn: (row) =>
          row.preco_medio_m2 * row.area_total_terreno * (row.percentual_comissao / 100),
        header: 'Valor da comissão',
        cell: ({row}) =>
          row.original.percentual_comissao
            ? formatNumber(
                row.original.preco_medio_m2 *
                  row.original.area_total_terreno *
                  (row.original.percentual_comissao / 100),
                'decimal'
              )
            : '-',
      },
      {
        id: 'operacao_urbana',
        accessorFn: (row) => getPrimeiroTerreno(row)?.operacao_urbana ?? '',
        header: 'Op. Urbana',
        cell: ({row}) => {
          const terreno = getPrimeiroTerreno(row.original)

          return terreno ? (
            terreno.operacao_urbana ? (
              <div data-bs-toggle='tooltip' data-bs-placement='top' title={terreno.operacao_urbana}>
                <BiInfoCircle className='fs-1'></BiInfoCircle>SIM
              </div>
            ) : (
              'Não'
            )
          ) : (
            '-'
          )
        },
      },
      {
        id: 'responsavel',
        accessorFn: (row) => row.users?.name ?? '-',
        header: 'Responsável',
        cell: ({getValue}) => getValue() as React.ReactNode,
      },
      {
        id: 'corretor',
        accessorFn: (row) => {
          const corretores: string[] = []

          row.empreendimento_terreno?.forEach((item) => {
            item.terreno?.terreno_corretor?.forEach((corretor) => {
              if (corretor.corretor.nome !== 'Sem corretor') {
                corretores.push(`${corretor.corretor.nome}(${corretor.corretor.telefone})`)
              }
            })
          })

          return corretores.join('/') || ''
        },
        rowwrap: true,
        header: 'Corretor',
        width: 200,
        cell: ({getValue}) => getValue() as React.ReactNode,
      },
       {
        id: 'bairro',
        accessorFn: (row) => row.empreendimento_terreno?.[0]?.terreno?.bairro ?? '-',
        header: 'Bairro',
        cell: ({getValue}) => getValue() as React.ReactNode,
      },
    ],
    []
  )

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
      columnOrder,
      columnVisibility,
    },
    initialState: {
      pagination: {
        pageSize: 50,
      },
    },
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    filterFns: undefined,
  })

  const getColumnLabel = (columnId: string) => {
    const columnHeader = table.getColumn(columnId)?.columnDef.header

    return typeof columnHeader === 'string' ? columnHeader : columnId
  }

  useEffect(() => {
    Cookies.set(
      TABLE_CONFIG_COOKIE,
      JSON.stringify({
        columnOrder,
        columnVisibility,
      }),
      COOKIE_OPTIONS
    )
  }, [columnOrder, columnVisibility])

  useEffect(() => {
    table.setPageIndex(0)
  }, [table, tableData])

  const onDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) {
      return
    }

    const visibleColumnIds = table.getVisibleLeafColumns().map((column) => column.id)
    const movingColumnId = visibleColumnIds[result.source.index]
    const targetColumnId = visibleColumnIds[result.destination.index]
    const nextColumnOrder = [...columnOrder]
    const currentIndex = nextColumnOrder.indexOf(movingColumnId)
    const targetIndex = nextColumnOrder.indexOf(targetColumnId)

    if (currentIndex === -1 || targetIndex === -1) {
      return
    }

    nextColumnOrder.splice(currentIndex, 1)
    nextColumnOrder.splice(targetIndex, 0, movingColumnId)
    setColumnOrder(nextColumnOrder)
  }

  function exportTable() {
    const dataToExport = []
    // eslint-disable-next-line array-callback-return
    data.GetEmpreendimentos.result.map((Response) => {
      const terreno = getPrimeiroTerreno(Response)

      dataToExport.push({
        nome: Response.nome,
        bairro:  Response?.empreendimento_terreno?.[0]?.terreno?.bairro ? Response?.empreendimento_terreno?.[0]?.terreno?.bairro : '-',
        'Data Cadastro': Response.created_at ? formatDate(Response.created_at) : '-',
        Temperatura: Response.empreendimento_temperatura?.nome,
        'Qtd. Lotes': Response?.empreendimento_terreno?.length,
        'Área do terreno (m²)': Response?.area_total_terreno
          ? formatNumber(Response?.area_total_terreno, 'decimal')
          : '-',
        'Área total (m²)': Response?.area_total_privativa
          ? formatNumber(Response?.area_total_privativa, 'decimal')
          : '-',
        Zoneamento: terreno ? (terreno.zoneamento ? terreno.zoneamento : '-') : '-',
        Status: Response.empreendimento_situacao?.nome,
        'Operação Urbana': terreno
          ? terreno.operacao_urbana
            ? terreno.operacao_urbana
            : '-'
          : '-',
        VGV: Response?.vgv ? formatNumber(Response?.vgv, 'currency') : '-',
        'VGV Próprio': Response?.vgv_proprio ? formatNumber(Response?.vgv_proprio , 'currency') : '-',
        'VGV-VP': Response.vgv_vp ? formatNumber(Response.vgv_vp, 'currency') : '-',
        'Terreno/VGV': Response.vgv
          ? formatNumber(
              ((Response.preco_medio_m2 * Response.area_total_terreno) / (Response.vgv || 1)) * 100,
              'inteiro'
            ) + '%'
          : '-',
        'Terreno em dinheiro/VGV': Response.vgv_vp
          ? formatNumber((Response.terreno_em_dinheiro / (Response.vgv || 1)) * 100, 'inteiro') +
            '%'
          : '-',
        'Cadastrado por': Response.users ? (Response.users.name ? Response.users.name : '-') : '-',
        'Corretores':()=>{  const corretores: string[] = []

          Response.empreendimento_terreno?.forEach((item) => {
            item.terreno?.terreno_corretor?.forEach((corretor) => {
              if (corretor.corretor.nome !== 'Sem corretor') {
                corretores.push(`${corretor.corretor.nome}(${corretor.corretor.telefone})`)
              }
            })
          })

          return corretores.join('/') || ''
        },
        'Terreno em dinheiro': formatNumber(Response.terreno_em_dinheiro, 'currency'),
        'Valor da comissão': ({row}) =>{        
           return row.original.percentual_comissao
            ? formatNumber(
                row.original.preco_medio_m2 *
                  row.original.area_total_terreno *
                  (row.original.percentual_comissao / 100),
                'decimal'
              )
            : '-'
          }
      })
    })
    ExportTable(dataToExport, '', 'Relatório Empreendimentos.xlsx')
  }

  const renderToolbar = (loading = false) => (
    <div
      className='card-toolbar all-end'
      data-bs-toggle='tooltip'
      data-bs-placement='top'
      data-bs-trigger='hover'
      title='Exportar table'
    >
      <button
        className='me-4 btn btn-sm btn-flex  btn-primary'
        onClick={exportTable}
        disabled={loading}
      >
        <BiExport></BiExport>
        Exportar
      </button>

      <button
        className='me-4 btn btn-sm btn-flex btn-light btn-light-warning text-black'
        data-kt-menu-trigger='click'
        data-kt-menu-placement='bottom-end'
        data-kt-menu-flip='top-end'
        onClick={toggleInfos}
        disabled={loading}
      >
        <KTSVG
          path='/media/icons/duotune/general/gen046.svg'
          className='svg-icon-5 svg-icon-gray-500 me-1 text-black'
        />
        Infos
      </button>
      <button
        className='me-4 btn btn-sm btn-flex btn-light btn-secondary'
        data-kt-menu-trigger='click'
        data-kt-menu-placement='bottom-end'
        data-kt-menu-flip='top-end'
        onClick={toggleFilter}
        disabled={loading}
      >
        <KTSVG
          path='/media/icons/duotune/general/gen031.svg'
          className='svg-icon-5 svg-icon-gray-500 me-1'
        />
        Filtros
      </button>

      <div className='dropdown'>
        <button
          className='me-4 btn btn-sm btn-flex btn-light btn-secondary'
          type='button'
          data-bs-toggle='dropdown'
          aria-expanded='false'
          disabled={loading}
        >
          <KTSVG
            path='/media/icons/duotune/general/gen019.svg'
            className='svg-icon-5 svg-icon-gray-500 me-1'
          />
          Colunas
        </button>
        <div className='dropdown-menu p-4'>
          {table.getAllLeafColumns().map((column) => (
            <label
              key={column.id}
              className='form-check form-check-sm form-check-custom form-check-solid mb-3'
            >
              <input
                className='form-check-input'
                type='checkbox'
                checked={column.getIsVisible()}
                disabled={!column.getCanHide()}
                onChange={column.getToggleVisibilityHandler()}
              />
              <span className='form-check-label ms-2'>{getColumnLabel(column.id)}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  )

  const renderTablePagination = () => (
    <div className='d-flex align-items-center justify-content-between flex-wrap gap-3 px-2 py-4'>
      <div className='text-muted fw-bold'>{table.getFilteredRowModel().rows.length} registros</div>
      <div className='d-flex align-items-center gap-2'>
        <button
          className='btn btn-sm btn-light'
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}
        >
          {'<<'}
        </button>
        <button
          className='btn btn-sm btn-light'
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          {'<'}
        </button>
        <span className='fw-bold px-2'>
          {table.getState().pagination.pageIndex + 1} / {table.getPageCount() || 1}
        </span>
        <button
          className='btn btn-sm btn-light'
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          {'>'}
        </button>
        <button
          className='btn btn-sm btn-light'
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
          disabled={!table.getCanNextPage()}
        >
          {'>>'}
        </button>
        <select
          className='form-select form-select-sm w-auto'
          value={table.getState().pagination.pageSize}
          onChange={(event) => table.setPageSize(Number(event.target.value))}
        >
          {[10, 25, 50, 100].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              {pageSize}
            </option>
          ))}
        </select>
      </div>
    </div>
  )

  // Tratativa de loading
  if (!data) {
    return (
      <div className='card  px-10 py-6'>
        <div className='border-0'>
          <div className='row'>
            <div className='col-xl-12' style={{minWidth: 0, maxWidth: '100%'}}>
              <div className='card-xxl-stretch '>
                <div className='card-header border-0    p-0'>
                  <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bolder fs-3 mb-1'>
                      <KTSVG
                        path='/media/icons/duotune/files/fil003.svg'
                        className='svg-icon-1 svg-icon-gray-500 me-1'
                      />
                      Relatórios do Empreendimento
                    </span>
                  </h3>
                  {renderToolbar(true)}
                </div>
                {/* end::Header */}
                <div className='separator my-2'></div>
                <Loader text={'caregando...'}></Loader>
              </div>
              <div className='card-footer all-end'>
                <Pagination setPages={setPages} pages={pages} total={100} />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {' '}
      <div className='card  px-10 py-6' 
      style={{maxWidth: tableContainerWidth, overflow: 'hidden'}}
      >
        <div className='border-0' 
        // style={{minWidth: 0, maxWidth: tableContainerWidth}}
        >
          <div className='row'
          //  style={{minWidth: 0, maxWidth: tableContainerWidth}}
           >
            <div className='col-xl-12' style={{minWidth: 0, maxWidth: tableContainerWidth}}>
              <div className='card-header border-0    p-0'>
                <h3 className='card-title align-items-start flex-column'>
                  <span className='card-label fw-bolder fs-3 mb-1'>
                    <KTSVG
                      path='/media/icons/duotune/files/fil003.svg'
                      className='svg-icon-1 svg-icon-gray-500 me-1'
                    />
                    Relatórios do Empreendimento
                  </span>
                </h3>
                {renderToolbar()}
              </div>
              {/* end::Header */}
              <div className='separator my-2'></div>
              {/* begin::Body */}
              <div
                className='card-body p-0'
                style={{minWidth: 0, width: tableContainerWidth, maxWidth: '100%'}}
              >
                {/* begin::Table container */}
                <div
                  className='table-responsive'
                  style={{
                    display: 'block',
                    width: tableContainerWidth,
                    maxWidth: '100%',
                    overflowX: 'auto',
                    overflowY: 'visible',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  <table
                    className='table table-striped table-row-dashed gy-2 gs-6 table-row-bordered table-hover table-row-gray-300 align-middle gs-0 gy-4'
                    style={{minWidth: 1800, width: 'max-content'}}
                  >
                    <thead>
                      <DragDropContext onDragEnd={onDragEnd}>
                        {table.getHeaderGroups().map((headerGroup) => (
                          <Droppable
                            key={headerGroup.id}
                            droppableId='table-relatorio-empreendimento-columns'
                            direction='horizontal'
                          >
                            {(provided) => (
                              <tr
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className='fw-bold fs-6 text-gray-800 border-bottom border-gray-200'
                              >
                                {headerGroup.headers.map((header, index) => (
                                  <Draggable
                                    key={header.id}
                                    draggableId={header.column.id}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <th
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        style={{
                                          whiteSpace: 'nowrap',
                                          background: snapshot.isDragging ? '#f5f8fa' : undefined,
                                          ...provided.draggableProps.style,
                                        }}
                                      >
                                        <div className='d-flex align-items-center gap-2'>
                                          <span
                                            {...provided.dragHandleProps}
                                            className='text-muted'
                                            style={{cursor: 'grab'}}
                                            title='Arrastar coluna'
                                          >
                                            ::
                                          </span>
                                          <span
                                            className={
                                              header.column.getCanSort()
                                                ? 'cursor-pointer select-none'
                                                : ''
                                            }
                                            onClick={header.column.getToggleSortingHandler()}
                                          >
                                            {header.isPlaceholder
                                              ? null
                                              : flexRender(
                                                  header.column.columnDef.header,
                                                  header.getContext()
                                                )}
                                            {{
                                              asc: ' ▲',
                                              desc: ' ▼',
                                            }[header.column.getIsSorted() as string] ?? null}
                                          </span>
                                        </div>
                                      </th>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </tr>
                            )}
                          </Droppable>
                        ))}
                      </DragDropContext>
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id} style={{whiteSpace: 'nowrap'}}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {renderTablePagination()}
                </div>
                {/* end::Table container */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export {Table}
